// Query-key factories - generated, never hand-written. Prefix invalidation
// works because TanStack Query matches by key prefix by default.
// See https://tanstack.com/query/latest/docs/framework/react/guides/query-invalidation

export const taskKeys = {
  all: ['tasks'],
  lists: () => [...taskKeys.all, 'list'],
  list: (filters = {}) => [...taskKeys.lists(), filters],
  detail: (id) => [...taskKeys.all, 'detail', id],
  comments: (id) => [...taskKeys.detail(id), 'comments'],
  // Under 'tasks', so every task change refreshes the projected occurrences too.
  occurrences: (range = {}) => [...taskKeys.all, 'occurrences', range],
};

export const projectKeys = {
  all: ['projects'],
  lists: () => [...projectKeys.all, 'list'],
  list: (filters = {}) => [...projectKeys.lists(), filters],
  detail: (id) => [...projectKeys.all, 'detail', id],
  sections: (id) => [...projectKeys.detail(id), 'sections'],
  activity: (id, params = {}) => [...projectKeys.detail(id), 'activity', params],
};

export const habitKeys = {
  all: ['habits'],
  lists: () => [...habitKeys.all, 'list'],
  list: (filters = {}) => [...habitKeys.lists(), filters],
  detail: (id) => [...habitKeys.all, 'detail', id],
  scheduledEvents: () => [...habitKeys.all, 'scheduled-events'],
};

export const labelKeys = {
  all: ['labels'],
  lists: () => [...labelKeys.all, 'list'],
};

export const customStatusKeys = {
  all: ['custom-statuses'],
  lists: () => [...customStatusKeys.all, 'list'],
  list: (teamId = null) => [...customStatusKeys.lists(), teamId],
};

export const customFieldKeys = {
  all: ['custom-fields'],
  lists: () => [...customFieldKeys.all, 'list'],
  list: (projectId = null) => [...customFieldKeys.lists(), projectId],
};

export const settingsKeys = {
  all: ['settings'],
  current: () => [...settingsKeys.all, 'current'],
  analytics: () => [...settingsKeys.all, 'analytics'],
  timeBreakdown: () => [...settingsKeys.all, 'time-breakdown'],
};

export const notificationKeys = {
  all: ['notifications'],
  lists: () => [...notificationKeys.all, 'list'],
  list: (params = {}) => [...notificationKeys.lists(), params],
  settings: () => [...notificationKeys.all, 'settings'],
};

// One cache entry holds every unread indicator for the user, so rendering a
// badge in a thousand rows costs one request, not a thousand.
export const unreadKeys = {
  all: ['unreads'],
  summary: () => [...unreadKeys.all, 'summary'],
};

export const documentKeys = {
  all: ['documents'],
  lists: () => [...documentKeys.all, 'list'],
  list: (filters = {}) => [...documentKeys.lists(), filters],
  detail: (id) => [...documentKeys.all, 'detail', id],
};

export const databaseKeys = {
  all: ['databases'],
  fields: (docId) => [...databaseKeys.all, 'fields', docId],
  rows: (docId) => [...databaseKeys.all, 'rows', docId],
  derived: (docId) => [...databaseKeys.all, 'derived', docId],
  views: (docId) => [...databaseKeys.all, 'views', docId],
  people: (docId) => [...databaseKeys.all, 'people', docId],
  rowsSummary: (docId, q = '') => [...databaseKeys.all, 'rows-summary', docId, q],
};

export const meetingKeys = {
  all: ['meetings'],
  lists: () => [...meetingKeys.all, 'list'],
};

export const teamKeys = {
  all: ['teams'],
  lists: () => [...teamKeys.all, 'list'],
  detail: (id) => [...teamKeys.all, 'detail', id],
  invites: (id) => [...teamKeys.detail(id), 'invites'],
  tasks: (id) => [...teamKeys.detail(id), 'tasks'],
};

export const focusTimeKeys = {
  all: ['focus-time'],
  current: () => [...focusTimeKeys.all, 'current'],
  scheduledEvents: () => [...focusTimeKeys.all, 'scheduled-events'],
};

export const trashKeys = {
  all: ['trash'],
  lists: () => [...trashKeys.all, 'list'],
  count: () => [...trashKeys.all, 'count'],
};

export const webhookKeys = {
  all: ['webhooks'],
  lists: () => [...webhookKeys.all, 'list'],
  detail: (id) => [...webhookKeys.all, 'detail', id],
  events: (id) => [...webhookKeys.detail(id), 'events'],
};

export const taskTypeKeys = {
  all: ['task-types'],
  lists: () => [...taskTypeKeys.all, 'list'],
};

export const gmailKeys = {
  all: ['gmail'],
  status: () => [...gmailKeys.all, 'status'],
  messages: (params = {}) => [...gmailKeys.all, 'messages', params],
  message: (id) => [...gmailKeys.all, 'message', id],
  threads: () => [...gmailKeys.all, 'threads'],
  rules: () => [...gmailKeys.all, 'rules'],
};

export const adminKeys = {
  all: ['admin'],
  users: (params = {}) => [...adminKeys.all, 'users', params],
  user: (id) => [...adminKeys.all, 'user', id],
  overview: (days) => [...adminKeys.all, 'overview', days],
  audit: () => [...adminKeys.all, 'audit'],
};

export const billingKeys = {
  all: ['billing'],
  subscription: () => [...billingKeys.all, 'subscription'],
};

export const googleCalendarKeys = {
  all: ['google-calendar'],
  status: () => [...googleCalendarKeys.all, 'status'],
  calendars: () => [...googleCalendarKeys.all, 'calendars'],
  events: (range = {}) => [...googleCalendarKeys.all, 'events', range],
};

export const mcpTokenKeys = {
  all: ['mcp-tokens'],
  lists: () => [...mcpTokenKeys.all, 'list'],
  connections: () => [...mcpTokenKeys.all, 'connections'],
};

export const aiKeys = {
  all: ['ai'],
  conversations: () => [...aiKeys.all, 'conversations'],
  conversationsList: (q = '') => [...aiKeys.conversations(), 'list', q],
  conversation: (id) => [...aiKeys.conversations(), 'detail', id],
  messages: (id) => [...aiKeys.conversation(id), 'messages'],
  usage: () => [...aiKeys.all, 'usage'],
};

export const botKeys = {
  all: ['bots'],
  list: (teamId = null) => [...botKeys.all, 'list', teamId],
  mine: () => [...botKeys.all, 'mine'],
  activity: () => [...botKeys.all, 'activity'],
  detail: (id) => [...botKeys.all, 'detail', id],
  models: (params = {}) => [...botKeys.all, 'models', params],
  aiSettings: (teamId = null) => [...botKeys.all, 'ai-settings', teamId],
  byok: () => [...botKeys.all, 'byok'],
  myInstructions: () => [...botKeys.all, 'my-instructions'],
  myDefaultModel: () => [...botKeys.all, 'my-default-model'],
  project: (projectId) => [...botKeys.all, 'project', projectId],
  chats: (botId, projectId) => [...botKeys.all, 'chats', botId, projectId],
  chatSearch: (botId, projectId, q) => [...botKeys.all, 'chats', botId, projectId, 'search', q],
  // Under 'chats': whatever refreshes a bot's chats refreshes the one history too.
  allChats: (filters = {}) => [...botKeys.all, 'chats', '*', filters],
  chat: (chatId) => [...botKeys.all, 'chat', chatId],
  chatCounts: () => [...botKeys.all, 'chat-counts'],
  projectChats: (projectId, q = '') => [...botKeys.all, 'project-chats', projectId, q],
  memory: (projectId) => [...botKeys.all, 'memory', projectId],
  taskStatus: (taskId) => [...botKeys.all, 'task-status', taskId],
  connections: (botId, projectId = null) => [...botKeys.all, 'connections', botId, projectId],
  credits: (teamId = null) => [...botKeys.all, 'credits', teamId],
  learning: (projectId) => [...botKeys.all, 'learning', projectId],
  skillHistory: (skillId) => [...botKeys.all, 'skill-history', skillId],
  profile: () => [...botKeys.all, 'profile'],
  secrets: (projectId) => [...botKeys.all, 'secrets', projectId],
  schedules: (botId, projectId) => [...botKeys.all, 'schedules', botId, projectId],
  allSchedules: () => [...botKeys.all, 'schedules', 'all'],
  scheduleRuns: (id) => [...botKeys.all, 'schedules', 'runs', id],
  workspace: (projectId) => [...botKeys.all, 'workspace', projectId],
  paused: (projectId) => [...botKeys.all, 'paused', projectId],
  servicePaused: () => [...botKeys.all, 'service-paused'],
  adminUsage: (days) => [...botKeys.all, 'admin-usage', days],
  workspaceFiles: (projectId, path) => [...botKeys.all, 'workspace-files', projectId, path],
};
